import React, { useEffect, useState } from "react";
import {
  riskAllocationMapping,
  useApiAugmentedScenariosCoefficientsReport,
  useFutureScenarioIncome,
  useReportBespoke,
  useScenariosCoefficients,
} from "@iliotech/data-wire";
import { ScenarioCoefficientsGrouping } from "@iliotech/generated-api-v3";
import { ScenarioValuesGrouping } from "@iliotech/generated-api-v3";
import { useParams } from "react-router-dom";

export const SCENARIO_CATEGORIES = [
  "Sector",
  "Asset Class",
  "Country",
  "Currency",
  // "Custodian",
];

export interface ExtraCellProps {
  sliderValues: RiskImpacts;
  selectedCategory: string;
  shouldResetPaging: boolean;
}

const getRandomValue = (min: number, max: number) => {
  return Math.round((Math.random() * (max - min) + min) * 10) / 10;
};

const generateRandomValues = (): RiskImpacts => ({
  price: getRandomValue(-20, 20),
  fx: getRandomValue(-10, 10),
  yield: getRandomValue(-50, 50),
  volatility: getRandomValue(-50, 50),
  months: 0,
});
const initialSliderValues = {
  price: 0,
  fx: 0,
  yield: 0,
  volatility: 0,
  months: 0,
};
export const useScenarioManagerWidget = (cellId: string, pageId: string) => {
  const { portfolioId } = useParams();
  const scenarioCoefficients = useScenariosCoefficients(portfolioId!);
  const store = useReportBespoke();

  const extraOptions = store.pages[pageId].cells[cellId]
    .extraOptions as ExtraCellProps;

  const sliderValues = extraOptions?.sliderValues ?? initialSliderValues;
  const selectedCategory = extraOptions?.selectedCategory ?? "Asset Class";
  const shouldResetPaging = extraOptions?.shouldResetPaging ?? false;

  const apiAugmentedScenariosCoefficients =
    useApiAugmentedScenariosCoefficientsReport(
      portfolioId!,
      sliderValues,
      false
    );

  const futureIncome = useFutureScenarioIncome(
    portfolioId!,
    sliderValues?.months
  );
  const requiresApiValues =
    scenarioCoefficients?.data?.data?.requiresBackendCalculations ?? true;

  useEffect(() => {
    apiAugmentedScenariosCoefficients.refetch();
  }, [initialSliderValues]);

  const existingExtraOptions = {
    sliderValues,
    selectedCategory,
    shouldResetPaging,
  };

  const setShouldResetPaging = (value: boolean) =>
    store.updateCell(pageId, cellId, "extraOptions", {
      ...existingExtraOptions,
      shouldResetPaging: value,
    });

  const onSliderValueChange = (key: string, value: number) =>
    store.updateCell(pageId, cellId, "extraOptions", {
      ...existingExtraOptions,
      sliderValues: { ...sliderValues, [key]: value },
    });

  const onCategoryChange = (category: string) => {
    store.updateCell(pageId, cellId, "extraOptions", {
      ...existingExtraOptions,
      selectedCategory: category,
    });
  };
  const maps = React.useMemo(() => {
    const multipliers = {
      priceMultiplier: sliderValues.price,
      yieldMultiplier: sliderValues.yield,
      fxMultiplier: sliderValues.fx,
      volatilityMultiplier: sliderValues.volatility,
    };

    if (requiresApiValues) {
      return riskAllocationMapping(
        apiAugmentedScenariosCoefficients?.data?.data,
        true,
        multipliers
      );
    }
    return riskAllocationMapping(
      scenarioCoefficients?.data?.data,
      false,
      multipliers
    );
  }, [
    scenarioCoefficients?.data?.data,
    apiAugmentedScenariosCoefficients?.data?.data,
    requiresApiValues,
  ]);

  const categoryMap = new Map([
    ...maps.sectorsMaps.categoryMap,
    ...maps.assetClassesMaps.categoryMap,
    ...maps.countriesMaps.categoryMap,
    ...maps.currenciesMaps.categoryMap,
    ...maps.custodiansMaps.categoryMap,
  ]);
  const AllocationMap = new Map([
    ...maps.sectorsMaps.allocationMap,
    ...maps.assetClassesMaps.allocationMap,
    ...maps.countriesMaps.allocationMap,
    ...maps.currenciesMaps.allocationMap,
    ...maps.custodiansMaps.allocationMap,
  ]);

  const selectedAllocation = AllocationMap.get(selectedCategory);
  const impacts = requiresApiValues
    ? getImpactsFromBackendData(selectedAllocation as any)
    : getImpactsFromCoefficients(selectedAllocation as any, sliderValues);

  const { pnl: pnlImpact, fx: fxImpact, price: priceImpact } = impacts;
  const totalMarketValueBase = requiresApiValues
    ? apiAugmentedScenariosCoefficients?.data?.data?.totalMarketValueBase ?? 1
    : scenarioCoefficients?.data?.data?.totalMarketValueBase ?? 1;

  const percentageImpact = ((pnlImpact ?? 0) / totalMarketValueBase) * 100;

  const handleChangeCategory = (event: any) => {
    onCategoryChange(event.target.value);
    setShouldResetPaging(true);
  };

  const onChangeSlider = (field: keyof RiskImpacts) => (value: number) => {
    onSliderValueChange(field, Math.round(value));
  };

  const resetSliders = () => {
    Object.keys(sliderValues).forEach((key) => {
      onSliderValueChange(key, 0);
    });
  };

  return {
    resetSliders,
    shouldResetPaging,
    setShouldResetPaging,
    selectedAllocation,
    handleChangeCategory,
    onChangeSlider,
    sliderValues,
    selectedCategory,
    onCategoryChange,
    benchmarkName: "Benchmark",
    portfolioCurrency: "USD",
    affectedByYieldChange:
      scenarioCoefficients?.data?.data?.affectedByYieldChange,
    affectedByVolatilityChange:
      scenarioCoefficients?.data?.data?.affectedByVolatilityChange,
    impacts: {
      pnlImpact: pnlImpact ?? 0,
      priceImpact,
      fxImpact,
    },
    percentageImpact,
    categoryMap,
    refetchData: apiAugmentedScenariosCoefficients.refetch,
  };
};

function getImpactsFromBackendData(
  allocation: ScenarioValuesGrouping | undefined
) {
  const impacts: ImpactTotals & { pnl?: number } = {
    price: allocation?.totalPriceValue ?? 0,
    fx: allocation?.totalFxValue ?? 0,
    // pnl: (allocation?.totalMarketValueBase ?? 0),
  };

  impacts.pnl = Object.values(impacts).reduce((acc, curr) => acc + curr);

  return impacts;
}

function getImpactsFromCoefficients(
  allocation: ScenarioCoefficientsGrouping | undefined,
  sliderValues: RiskImpacts
) {
  const impacts: ImpactTotals & { pnl?: number } = {
    price: 0,
    fx: 0,
  };

  allocation?.entries.forEach((entry) => {
    impacts.price += (entry.priceMultiplier ?? 0) * (sliderValues.price ?? 0);
    impacts.price += (entry.yieldMultiplier ?? 0) * (sliderValues.yield ?? 0);
    impacts.fx += (entry.fxMultiplier ?? 0) * (sliderValues.fx ?? 0);
    // vol is always 0 if we're calculating from coefficients. The backend uses black scholes if there are options in the portfolio
  });

  impacts.pnl = Object.values(impacts).reduce((acc, curr) => acc + curr);
  return impacts;
}

export type RiskImpacts = {
  price: number;
  fx: number;
  yield: number;
  volatility: number;
  months: number;
};
export type ImpactTotals = { price: number; fx: number; pnl?: number };
